import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";


function secondsRemaining(expiresAt) {
  if (!expiresAt) return 0;
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
}


export function usePortalPasswordReset() {
  const [stage, setStage] = useState("email");
  const [resetId, setResetId] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otpPreview, setOtpPreview] = useState("");
  const [expiresAt, setExpiresAt] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const startExpiryTimer = useCallback((expiresIn) => {
    const nextExpiry = Date.now() + Number(expiresIn || 60) * 1000;
    setExpiresAt(nextExpiry);
    setCountdown(secondsRemaining(nextExpiry));
  }, []);

  useEffect(() => {
    if (!expiresAt || stage !== "otp") return undefined;
    const timer = window.setInterval(() => {
      setCountdown(secondsRemaining(expiresAt));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAt, stage]);

  const run = useCallback(async (action) => {
    setLoading(true);
    setError("");
    try {
      return await action();
    } catch (requestError) {
      setError(formatApiError(requestError.response?.data?.detail) || "Permintaan gagal. Silakan coba kembali.");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const requestOtp = useCallback(async (email) => {
    const data = await run(async () => {
      const response = await api.post("/portal/password-reset/request", { email });
      return response.data;
    });
    if (!data) return false;
    setResetId(data.reset_id);
    setMaskedEmail(data.masked_email);
    setOtpPreview(data.otp_preview || "");
    startExpiryTimer(data.expires_in);
    setStage("otp");
    return true;
  }, [run, startExpiryTimer]);

  const resendOtp = useCallback(async () => {
    const data = await run(async () => {
      const response = await api.post("/portal/password-reset/resend", { reset_id: resetId });
      return response.data;
    });
    if (!data) return false;
    setOtpPreview(data.otp_preview || "");
    startExpiryTimer(data.expires_in);
    return true;
  }, [resetId, run, startExpiryTimer]);

  const verifyOtp = useCallback(async (otp) => {
    const data = await run(async () => {
      const response = await api.post("/portal/password-reset/verify", { reset_id: resetId, otp });
      return response.data;
    });
    if (!data) return false;
    setResetToken(data.reset_token);
    setExpiresAt(0);
    setCountdown(0);
    setStage("password");
    return true;
  }, [resetId, run]);

  const completeReset = useCallback(async (newPassword, confirmPassword) => {
    const data = await run(async () => {
      const response = await api.post("/portal/password-reset/complete", {
        reset_id: resetId,
        reset_token: resetToken,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      return response.data;
    });
    return Boolean(data);
  }, [resetId, resetToken, run]);

  return {
    stage,
    maskedEmail,
    otpPreview,
    countdown,
    loading,
    error,
    setError,
    requestOtp,
    resendOtp,
    verifyOtp,
    completeReset,
  };
}

